import { sendGAEvent } from '@next/third-parties/google'
import airbridge from 'airbridge-web-sdk-loader'
import { getCookie } from 'cookies-next'
import { usePlausible } from 'next-plausible'
import { usePostHog } from 'posthog-js/react'
import { useCallback } from 'react'
import { useIntercom } from 'react-use-intercom'
import useAuthUser from '~/src/hooks/useAuthUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import { stringToHash } from '~/src/lib/urlHelpers'

const useAnalytics = () => {
  const plausible = usePlausible()
  const posthog = usePostHog()
  const { update } = useIntercom()
  const { tempUserId } = useGetTempUserId()
  const { authUser } = useAuthUser()

  const track = useCallback(
    (
      event: string,
      properties?: Record<string, any>,
      options?: {
        bypassAuth?: boolean
        skipGA?: boolean
        skipPosthog?: boolean
        sendToPlausible?: boolean
        sendToTinybird?: boolean
        sendToFb?: boolean
        sendToPodscribe?: boolean
      }
    ) => {
      const __dupe_temp_user_id = tempUserId
      const __dupe_auth_user_id = authUser?.uid

      const _ga = getCookie('_ga')
      const _gcl_aw = getCookie('_gcl_aw')

      if (process.env.NODE_ENV === 'development') {
        console.log('%c****** useAnalytics.track', 'color:blue', {
          event,
          properties,
          __dupe_temp_user_id,
          __dupe_auth_user_id,
          _ga,
          _gcl_aw,
        })

        return
      }

      if (__dupe_temp_user_id) {
        properties = {
          ...properties,
          __dupe_temp_user_id,
        }
      }

      if (__dupe_auth_user_id) {
        properties = {
          ...properties,
          __dupe_auth_user_id,
        }
      }

      if (_ga) {
        properties = {
          ...properties,
          _ga,
        }
      }

      if (_gcl_aw) {
        properties = {
          ...properties,
          _gcl_aw,
        }
      }

      // Google Analytics
      if (!options?.skipGA) {
        sendGAEvent('event', event, { ...properties })
      }

      // PostHog
      if (!options?.skipPosthog) {
        posthog.capture(event, properties)
      }

      // Facebook Pixel
      if (options?.sendToFb) {
        window.fbq('trackCustom', event, properties)
      }

      // Plausible
      if (options?.sendToPlausible) {
        plausible(event, {
          props: properties,
        })
      }

      // Tinybird
      if (
        options?.sendToTinybird &&
        typeof window !== 'undefined' &&
        window?.Tinybird
      ) {
        window.Tinybird.trackEvent(event, properties || {})
      }

      // Podscribe
      if (options?.sendToPodscribe && window?.podscribe) {
        const hashedEmail = authUser?.email
          ? stringToHash(authUser.email)
          : null

        window?.podscribe?.('lead', {
          device_id: authUser?.uid || tempUserId,
          hashed_email: hashedEmail,
        })
      }
    },
    [plausible, posthog, tempUserId, authUser]
  )

  const identify = useCallback(
    (userId: string, properties?: Record<string, any>) => {
      // const alreadyIdentified = posthog._isIdentified()

      // if (alreadyIdentified) {
      //   logger.debug('🔍🔍🔍 Already identified - skipping', {
      //     userId,
      //     properties,
      //   })

      //   return
      // }

      // if (process.env.NODE_ENV === 'development') {
      //   // console.log('%c****** useAnalytics.identify', 'color:purple', {
      //   //   userId,
      //   //   properties,
      //   // })

      //   return
      // }

      // PostHog Identify
      posthog.identify(userId, properties)

      // Airbridge
      airbridge.setUserID(userId)

      // Intercom
      update({
        userId,
        email: properties?.email,
        phone: properties?.phone,
        customAttributes: {
          tempUserIds: properties?.tempUserIds,
        },
      })

      // Podscribe
      if (window?.podscribe) {
        const hashedEmail = properties?.email
          ? stringToHash(properties.email)
          : null

        window?.podscribe('signup', {
          device_id: userId,
          hashed_email: hashedEmail,
        })
      }
    },
    [posthog, update]
  )

  const getPostHogDistinctId = useCallback(() => {
    try {
      return posthog?.get_distinct_id()
    } catch (e) {
      return null
    }
  }, [posthog])

  const analyticsProxy = {
    identify,
    track,
    getPostHogDistinctId,
  }

  return analyticsProxy
}

export default useAnalytics
